import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '../lib/utils';

function withPreviewVersion(url: string, version: number) {
  if (url.startsWith('blob:') || url.startsWith('data:')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}preview=${version}`;
}

export function BeforeAfter({
  before,
  after,
  alt,
  priority = false,
  className,
  fit = 'cover',
  aspectRatio,
  autoPreview = false
}: {
  before: string;
  after: string;
  alt: string;
  priority?: boolean;
  className?: string;
  fit?: 'cover' | 'contain';
  aspectRatio?: number;
  autoPreview?: boolean;
}) {
  const initialPosition = autoPreview ? 46 : 50;
  const [position, setPosition] = useState(initialPosition);
  const [afterLoaded, setAfterLoaded] = useState(false);
  const [afterFailed, setAfterFailed] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(() => Date.now());
  const [autoPaused, setAutoPaused] = useState(false);
  const compareRef = useRef<HTMLDivElement>(null);
  const rangeRef = useRef<HTMLInputElement>(null);
  const positionRef = useRef(initialPosition);
  const resumeTimerRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const segmentRef = useRef({ from: initialPosition, to: 64, startedAt: 0 });
  const draggingRef = useRef(false);
  const dragFrameRef = useRef<number | null>(null);
  const pendingPositionRef = useRef<number | null>(null);

  function applyPosition(next: number, commitState = false) {
    const bounded = Math.min(100, Math.max(0, next));
    positionRef.current = bounded;
    compareRef.current?.style.setProperty('--position', `${bounded}%`);
    if (rangeRef.current) rangeRef.current.value = String(bounded);
    if (commitState) setPosition(bounded);
  }

  // Dragging writes straight to the DOM and is coalesced to one update per
  // animation frame. Committing React state on every pointer move re-rendered
  // the component mid-drag and re-applied a stale `--position` from props,
  // which is what made the divider step instead of glide.
  function scheduleDragPosition(next: number) {
    pendingPositionRef.current = next;
    if (dragFrameRef.current !== null) return;
    dragFrameRef.current = window.requestAnimationFrame(() => {
      dragFrameRef.current = null;
      if (pendingPositionRef.current !== null) applyPosition(pendingPositionRef.current);
    });
  }

  function positionFromClientX(clientX: number) {
    const element = compareRef.current;
    if (!element) return positionRef.current;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0) return positionRef.current;
    return ((clientX - rect.left) / rect.width) * 100;
  }

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    // Leave the overlay buttons alone.
    if ((event.target as HTMLElement).closest('button')) return;
    draggingRef.current = true;
    pauseAutoPreview();
    compareRef.current?.setPointerCapture(event.pointerId);
    scheduleDragPosition(positionFromClientX(event.clientX));
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!draggingRef.current) return;
    scheduleDragPosition(positionFromClientX(event.clientX));
  }

  function endDrag(event: ReactPointerEvent<HTMLDivElement>) {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (compareRef.current?.hasPointerCapture(event.pointerId)) {
      compareRef.current.releasePointerCapture(event.pointerId);
    }
    if (dragFrameRef.current !== null) {
      window.cancelAnimationFrame(dragFrameRef.current);
      dragFrameRef.current = null;
    }
    if (pendingPositionRef.current !== null) applyPosition(pendingPositionRef.current);
    pendingPositionRef.current = null;
    // Sync React once, at the end, so the quick-action buttons stay accurate.
    setPosition(positionRef.current);
  }

  useEffect(() => {
    const nextVersion = Date.now();
    const nextPosition = autoPreview ? 46 : 50;
    setPreviewVersion(nextVersion);
    setPosition(nextPosition);
    positionRef.current = nextPosition;
    segmentRef.current = { from: nextPosition, to: 60.5, startedAt: 0 };
    setAfterLoaded(false);
    setAfterFailed(false);

    applyPosition(nextPosition);

    const image = new Image();
    image.decoding = 'async';
    image.onload = () => setAfterLoaded(true);
    image.onerror = () => setAfterFailed(true);
    image.src = withPreviewVersion(after, nextVersion);
    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [after, before, autoPreview]);

  useEffect(() => {
    if (!autoPreview || autoPaused) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      || document.documentElement.dataset.reducedMotion === 'true';
    if (reducedMotion) return;

    const minimum = 39.5;
    const maximum = 60.5;
    const duration = 4600;
    const current = positionRef.current;
    segmentRef.current = {
      from: current,
      to: current >= 50 ? minimum : maximum,
      startedAt: performance.now()
    };

    const animate = (now: number) => {
      const segment = segmentRef.current;
      const progress = Math.min(1, Math.max(0, (now - segment.startedAt) / duration));
      const eased = 0.5 - Math.cos(progress * Math.PI) / 2;
      applyPosition(segment.from + (segment.to - segment.from) * eased);

      if (progress >= 1) {
        const nextTarget = segment.to <= minimum ? maximum : minimum;
        segmentRef.current = { from: segment.to, to: nextTarget, startedAt: now };
      }
      animationFrameRef.current = window.requestAnimationFrame(animate);
    };

    animationFrameRef.current = window.requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    };
  }, [autoPreview, autoPaused]);

  useEffect(() => () => {
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
    if (animationFrameRef.current) window.cancelAnimationFrame(animationFrameRef.current);
    if (dragFrameRef.current) window.cancelAnimationFrame(dragFrameRef.current);
  }, []);

  function pauseAutoPreview(resume = true) {
    if (!autoPreview) return;
    setAutoPaused(true);
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
    if (resume) {
      resumeTimerRef.current = window.setTimeout(() => setAutoPaused(false), 5200);
    }
  }

  function showPosition(next: number) {
    pauseAutoPreview();
    const element = compareRef.current;
    if (element) {
      // Only the button shortcuts animate; dragging must stay 1:1 with the cursor.
      element.classList.add('compare-animating');
      window.setTimeout(() => element.classList.remove('compare-animating'), 450);
    }
    applyPosition(next, true);
  }

  const afterSrc = withPreviewVersion(after, previewVersion);
  const loadingProps = priority
    ? ({ loading: 'eager', fetchPriority: 'high' } as const)
    : ({ loading: 'lazy' } as const);

  return (
    <div
      ref={compareRef}
      className={cn('compare', autoPreview && 'auto-preview', autoPaused && 'auto-preview-paused', className)}
      style={{ '--compare-fit': fit, '--compare-aspect-ratio': aspectRatio ? String(aspectRatio) : undefined } as CSSProperties}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <img
        src={afterSrc}
        alt={`${alt} final result`}
        className="compare-image compare-after-image"
        draggable={false}
        decoding="async"
        onLoad={() => setAfterLoaded(true)}
        onError={() => setAfterFailed(true)}
        {...loadingProps}
      />
      <div className="compare-before">
        <img
          src={before}
          alt={`${alt} uploaded original`}
          className="compare-image compare-original-image"
          draggable={false}
          decoding="async"
          {...loadingProps}
        />
      </div>

      {!afterLoaded && !afterFailed && <div className="compare-status">Loading final result…</div>}
      {afterFailed && <div className="compare-status compare-status-error">Final preview unavailable</div>}

      <div className="compare-preview-badge">Original upload · Final result</div>
      {!autoPreview && (
        <div className="compare-quick-actions" aria-label="Comparison view shortcuts">
          <button type="button" className={position === 100 ? 'active' : ''} onClick={() => showPosition(100)}>Original</button>
          <button type="button" className={position === 0 ? 'active' : ''} onClick={() => showPosition(0)}>Final</button>
        </div>
      )}
      {autoPreview && <div className="compare-drag-hint">Drag to compare</div>}

      <div className="compare-divider"><span /></div>
      <span className="compare-label before-label">Original</span>
      <span className="compare-label after-label">Final</span>
      <input
        ref={rangeRef}
        aria-label="Compare uploaded original and final result"
        type="range"
        min="0"
        max="100"
        step="0.1"
        defaultValue={initialPosition}
        onChange={(event) => {
          pauseAutoPreview();
          applyPosition(Number(event.target.value), true);
        }}
        onKeyDown={(event) => {
          pauseAutoPreview();
          const step = event.shiftKey ? 10 : 2;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            event.preventDefault();
            applyPosition(positionRef.current - step, true);
          } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            event.preventDefault();
            applyPosition(positionRef.current + step, true);
          }
        }}
      />
    </div>
  );
}
